/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";

import AlertModal from "../modals/AlertModal";
import BaseAddressModal from "../modals/BaseAddressModal";
import CancelDeliveryPickupReturnModal from "../modals/CancelDeliveryPickupReturnModal";
import ConfirmModal from "../modals/ConfirmModal";
import ConfirmReceiverAddressModal from "../modals/ConfirmReceiverAddressModal";
import ConvenienceBarcodeModal from "../modals/ConvenienceBarcodeModal";
import DeliveryCompletedImageModal from "../modals/DeliveryCompletedImageModal";
import DeliveryDelayModal from "../modals/DeliveryDelayModal";
import DeliveryLostModal from "../modals/DeliveryLostModal";
import DeliveryRouteModal from "../modals/DeliveryRouteModal";
import DeliverySentBackModal from "../modals/DeliverySentBackModal";
import ReallocationRidersModal from "../modals/ReallocationRidersModal";
import SelectSameReceiverDeliveriesModal from "../modals/SelectSameReceiverDeliveriesModal";
import SendMessageModal from "../modals/SendMessageModal";
import UpdateAddressModal from "../modals/UpdateAddressModal";
import UpdateCompletedLocationModal from "../modals/UpdateCompletedLocationModal";

import { getDistance } from "../models/Delivery";

import {
  useAuthStore,
  useBaseAddressesStore,
  useCsesStore,
  useDeliveriesStore,
  useLoadingStore,
  useLocationStore,
  useModalStore,
} from "../stores/hooks";
import { CS_TYPE_USER, CS_TYPE_CHAINLOGIS } from "../stores/Cses";

import { formatDate } from "../lib/date";

import {
  renderDelayReasonShortText,
  renderPickupDelayReasonShortText,
} from "../utils/delay";
import {
  COMPLETED_LOCATION_POSTBOX,
  CAPTURE_METHOD,
  shouldSendSms,
} from "../utils/delivery";

import {
  POST_MESSAGES_TYPES,
  goToCs,
  goToPickups,
  sendToastMessage,
  sendUpdatedMessage,
  sendSmsMessageForDirect,
} from "../utils/postMessages";
import { applySizeClass } from "../utils/fontSize";

const PASSWORD_TYPES = {
  CHAINLOGIS: "CHAINLOGIS",
  USER: "USER",
};

// RN 레포의 STATUS_DELIVERY_STARTED 값("DELIVERY_STARTED")과 일치해야 함 -
// 웹뷰 자체 상수(models/Delivery.js의 DELIVERY_STATUS_4)와는 다른 체계이니
// RN 쪽 값이 바뀌면 여기도 같이 바꿔야 함
// (deliveriesAllocated는 RN이 raw 값 그대로 push하는 데이터라 웹뷰 모델을 거치지 않음)
const RN_STATUS_DELIVERY_STARTED = "DELIVERY_STARTED";

// 작업 타입 (RN으로 전송될 deliveryType 값)
const ACTION_TYPE = {
  PICKUP: "pickup",
  DELIVERY: "delivery",
  REUPLOAD: "reupload",
  CANCEL_PICKUP: "cancel-pickup",
};

const InfoRow = ({ children }) => {
  return <dl className="flex justify-start items-start">{children}</dl>;
};

const InfoLabel = ({ children }) => {
  return <dt className="w-80 text-[#828182] text-16">{children}</dt>;
};

const InfoValue = ({ large, children }) => {
  return (
    <dd className={`flex-1 ${large ? "text-18 font-bold" : "text-16"}`}>
      {children}
    </dd>
  );
};

const ButtonsWrap = ({ children }) => {
  return (
    <div className="flex justify-start items-center gap-5">{children}</div>
  );
};

const ButtonSquare = ({ onClick, bgColor, children }) => {
  // 원본 SquareButton의 color="border" 대응: 흰 배경 + 테두리
  const isBorder = bgColor === "border";

  return (
    <button
      type="button"
      className={
        isBorder
          ? "text-18 bg-white text-black border border-gray-d py-4 px-12 flex-1"
          : `text-18 ${bgColor || "bg-orange"} text-white py-4 px-12 flex-1`
      }
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const ButtonRound = ({ onClick, type, minWidth, children }) => {
  let classNames = "bg-orange text-white";
  if (type === "ORANGE") {
    classNames = "bg-orange text-white";
  } else if (type === "MINT") {
    classNames = "bg-mint text-white";
  }

  return (
    <button
      type="button"
      className={`text-16 py-2 px-12 rounded-full ${classNames} ${minWidth}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

// ============================================
// 주소 정보 버튼
// ============================================

const BaseAddressButton = ({ delivery, type = "receiver", onAfterSubmit }) => {
  const { baseAddressesAll, isCollectionRestricted } =
    useBaseAddressesStore().state;
  const { openModal } = useModalStore();

  const handleClickBaseAddress = () => {
    if (isCollectionRestricted) {
      openModal(
        <AlertModal
          key="alert-modal"
          message="주소정보를 사용할 수 없는 주소지입니다."
        />,
      );
      return;
    }

    if (type === "sender" || !delivery.isReceiverAddressError) {
      openModal(
        <BaseAddressModal
          key="base-address-modal"
          delivery={delivery}
          type={type}
          onAfterSubmit={onAfterSubmit}
        />,
      );
    } else {
      openModal(
        <AlertModal
          key="alert-modal"
          message="위치등록 버튼을 터치하여 주소 등록 후 이용해주십시오."
        />,
      );
    }
  };

  return (
    <ButtonSquare bgColor="bg-mint" onClick={handleClickBaseAddress}>
      주소정보{baseAddressesAll?.length ? "(!)" : ""}
    </ButtonSquare>
  );
};

// ============================================
// 전화 버튼
// ============================================

const CallButton = ({ mobile, active, label, bookId }) => {
  const handleClickCall = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: POST_MESSAGES_TYPES.CALL,
          mobile,
          bookId,
        }),
      );
    } else {
      window.alert("통화에 실패했습니다. 다시 시도해주세요");
    }
  };

  return (
    <button
      type="button"
      className={`border-1 py-2 px-12 rounded-10 ml-5 min-w-60 text-16 ${
        active
          ? "bg-orange text-white border-orange"
          : "bg-white text-gray-a border-gray-d"
      }`}
      onClick={handleClickCall}
    >
      {label || "통화"}
    </button>
  );
};

// ============================================
// 직접등록건 취소 버튼
// ============================================

const CancelDeliveryButton = ({ delivery }) => {
  const { ...actions } = useDeliveriesStore();
  const { ...loadingActions } = useLoadingStore();
  const { openModal } = useModalStore();

  const handleClickCancelDelivery = () => {
    openModal(
      <ConfirmModal
        key="confirm-modal"
        message={`${delivery.bookId} 건을 취소하시겠습니까?`}
        onSubmit={cancelDelivery}
      />,
    );
  };

  const cancelDelivery = async () => {
    try {
      loadingActions.startLoading();

      const data = {
        nonDoobal: true,
      };

      await actions.cancel({
        bookId: delivery.bookId,
        data,
      });

      sendUpdatedMessage();

      openModal(
        <AlertModal
          key="alert-modal"
          message="취소처리 되었습니다."
          onSubmit={goToDeliveries}
        />,
      );

      loadingActions.finishLoading();
    } catch (e) {
      window.alert(`취소에 실패했습니다. ${e.message}`);
      loadingActions.finishLoading();
    }
  };

  const goToDeliveries = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: POST_MESSAGES_TYPES.NAVIGATION,
          to: "deliveries",
        }),
      );
    } else {
      window.alert("페이지 이동에 실패했습니다. 앱 종료후 다시 시도해주세요");
    }
  };

  return (
    <ButtonSquare bgColor="bg-mint" onClick={handleClickCancelDelivery}>
      직접등록건 취소
    </ButtonSquare>
  );
};

// ============================================
// 배송/반송/분실 완료 취소 버튼
// ============================================

const CancelDeliveryCompletedButton = ({ delivery, label, onAfterSubmit }) => {
  const { ...actions } = useDeliveriesStore();
  const { ...loadingActions } = useLoadingStore();

  const handleClickCancelDeliveryCompleted = async () => {
    if (
      window.confirm(
        `${delivery.bookId} ${delivery.statusString}를 취소하시겠습니까?`,
      )
    ) {
      try {
        loadingActions.startLoading();

        await actions.setPrevStatusBulk({
          bookIds: [delivery.bookId],
          status: delivery.statusNubmer,
        });

        sendUpdatedMessage();

        window.alert(
          `${delivery.bookId} ${delivery.statusString}를 취소했습니다. 실제 배송목록에서 배송건을 확인할 수 있습니다.`,
        );

        onAfterSubmit();
      } catch (e) {
        window.alert(
          `${delivery.statusString} 취소에 실패했습니다. ${e.message}`,
        );
        loadingActions.finishLoading();
      }
    }
  };

  return (
    <ButtonSquare
      bgColor="bg-orange"
      onClick={handleClickCancelDeliveryCompleted}
    >
      {label} 취소
    </ButtonSquare>
  );
};

// ============================================
// 배송 불가 지역 배차 취소 버튼
// ============================================

const CancelDeliveryNotSupportedButton = ({ delivery }) => {
  const { ...actions } = useDeliveriesStore();
  const { ...loadingActions } = useLoadingStore();
  const { openModal } = useModalStore();

  const handleClickCancelDeliveryNotSupported = () => {
    openModal(
      <ConfirmModal
        key="confirm-modal"
        message={`${delivery.bookId} 건을 배차취소하시겠습니까?`}
        onSubmit={cancelDeliveryNotSupported}
      />,
    );
  };

  const cancelDeliveryNotSupported = async () => {
    try {
      loadingActions.startLoading();

      const data = {
        nonDoobal: false,
      };

      await actions.cancel({
        bookId: delivery.bookId,
        data,
      });

      sendUpdatedMessage();

      openModal(
        <AlertModal
          key="alert-modal"
          message="취소처리 되었습니다."
          onSubmit={goToDeliveries}
        />,
      );

      loadingActions.finishLoading();
    } catch (e) {
      window.alert(`취소에 실패했습니다. ${e.message}`);
      loadingActions.finishLoading();
    }
  };

  const goToDeliveries = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: POST_MESSAGES_TYPES.NAVIGATION,
          to: "deliveries",
        }),
      );
    } else {
      window.alert("페이지 이동에 실패했습니다. 앱 종료후 다시 시도해주세요");
    }
  };

  return (
    <ButtonSquare
      bgColor="bg-mint"
      onClick={handleClickCancelDeliveryNotSupported}
    >
      배송불가지역 배차 취소
    </ButtonSquare>
  );
};

// ============================================
// 픽반품건 취소 버튼
// ============================================

const CancelDeliveryPickupReturnButton = () => {
  const handleClickCancelDeliveryPickupReturn = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: CAPTURE_METHOD.TAKE_PICTURE,
          deliveryType: ACTION_TYPE.CANCEL_PICKUP,
        }),
      );
    } else {
      window.alert("앱과의 통신에 실패했습니다. 다시 시도해주세요");
    }
  };

  return (
    <ButtonRound
      type="ORANGE"
      minWidth="min-w-[80px]"
      onClick={handleClickCancelDeliveryPickupReturn}
    >
      취소
    </ButtonRound>
  );
};

// ============================================
// 편의점 바코드 버튼
// ============================================

const ConvenienceBarcodeButton = ({ delivery }) => {
  const { openModal } = useModalStore();

  const handleClickConvenienceBarcode = () => {
    openModal(
      <ConvenienceBarcodeModal
        key="convenience-barcode-modal"
        delivery={delivery}
      />,
    );
  };

  return (
    <ButtonRound
      type="MINT"
      minWidth="min-w-[80px]"
      onClick={handleClickConvenienceBarcode}
    >
      GS25
    </ButtonRound>
  );
};

// ============================================
// CS 버튼
// ============================================

const CsButton = ({ type }) => {
  const handleClickCs = () => {
    goToCs({ csType: type });
  };

  return (
    <ButtonSquare bgColor="bg-orange" onClick={handleClickCs}>
      {type === CS_TYPE_USER ? "고객" : "회사"}CS
    </ButtonSquare>
  );
};

// ============================================
// 배송 완료 컴포넌트
// ============================================

const DeliveryCompleteComponent = ({ delivery }) => {
  const { ...actions } = useDeliveriesStore();
  const { ...loadingActions } = useLoadingStore();
  const { location } = useLocationStore().state;
  const { openModal, closeModal } = useModalStore();

  const [confirmedDistanceError, setConfirmedDistanceError] = useState(false);
  const [confirmedSigniture, setConfirmedSigniture] = useState(false);

  // 배송완료 위치 400미터 이내 확인 && 동일 배송건 조회
  const handleCheckDeliveryComplete = async ({ captureMethod }) => {
    const distance = getDistance(
      delivery.compareReceiverLocation?.latitude || 0,
      delivery.compareReceiverLocation?.longitude || 0,
      location?.latitude || 0,
      location?.longitude || 0,
    );

    if (!confirmedDistanceError && distance > 400) {
      openModal(
        <AlertModal
          key="alert-modal"
          title="완료 위치 재확인 요청"
          message={`주소지 위치와 휴대폰 기기 위치가 400m 이상 차이가 납니다. 배송지가 맞는지 재확인 후 배송완료 진행해주십시오.\n\n${
            delivery.displayAddress
          } ${
            delivery.receiverBuilding || ""
          }\n\n*임의완료는 위약사항으로 계약해지 사유이며, 이로 인해 회사에 손해발생 시 손해배상을 청구할 수 있습니다.`}
          onSubmit={() => {
            setConfirmedDistanceError(true);
          }}
        />,
      );

      return;
    }

    if (!confirmedSigniture && captureMethod === CAPTURE_METHOD.SIGNATURE) {
      openModal(
        <AlertModal
          key="alert-modal"
          message="배송완료 사진 미촬영 시 오배송, 분실 등 CS에 대한 증거자료 누락으로 배송원 귀책사항이 발생할 수 있습니다. 서명 완료 시 주의 부탁드리며 배송완료 사진 촬영하여 배송완료 진행해주십시오."
          onSubmit={() => {
            setConfirmedSigniture(true);
          }}
        />,
      );

      return;
    }

    // 사진촬영 방식이면서 kurlyProd 거래처일 때만 주소지 재확인 후 진행
    if (
      captureMethod === CAPTURE_METHOD.TAKE_PICTURE &&
      delivery.corpUser?.username === "kurlyProd"
    ) {
      openModal(
        <ConfirmReceiverAddressModal
          key="confirm-receiver-address-modal"
          delivery={delivery}
          onConfirm={() => proceedDeliveryComplete({ captureMethod })}
        />,
      );

      return;
    }

    proceedDeliveryComplete({ captureMethod });
  };

  // 동일 배송건 조회(배송중 상태만) 후 배송완료 진행
  const proceedDeliveryComplete = async ({ captureMethod }) => {
    if (delivery.statusDeliveryStart) {
      try {
        loadingActions.startLoading();

        const sameReceiverDeliveries =
          await actions.fetchSameReceiverDeliveries(delivery.bookId);

        loadingActions.finishLoading();

        if (sameReceiverDeliveries?.length > 0) {
          openModal(
            <SelectSameReceiverDeliveriesModal
              key="select-same-receiver-deliveries-modal"
              sameReceiverDeliveries={sameReceiverDeliveries}
              delivery={delivery}
              onAfterSubmit={handleSubmitWebview}
              captureMethod={captureMethod}
            />,
          );
        } else {
          handleSubmitWebview({ captureMethod });
        }
      } catch (e) {
        window.alert(`배송 건이 확인되지 않습니다. ${e.message}`);
        loadingActions.finishLoading();
      }
    } else {
      handleSubmitWebview({ captureMethod });
    }
  };

  // 앱과의 통신(서명, 사진촬영, 사진선택)
  const handleSubmitWebview = ({ captureMethod, bookIds }) => {
    closeModal();

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: captureMethod || CAPTURE_METHOD.SIGNATURE,
          deliveryType: ACTION_TYPE.DELIVERY,
          bookIds,
        }),
      );
    } else {
      window.alert("앱과의 통신에 실패했습니다. 다시 시도해주세요");
    }
  };

  if (delivery.spotKurlyDrink || delivery.mustSignature) {
    return (
      <ButtonsWrap>
        <DeliveryCompleteSignatureButton
          onCheck={handleCheckDeliveryComplete}
        />
      </ButtonsWrap>
    );
  }

  return (
    <>
      <ButtonsWrap>
        {/** 사진촬영 버튼 */}
        <DeliveryCompleteTakePictureButton
          onCheck={handleCheckDeliveryComplete}
        />
      </ButtonsWrap>
      <ButtonsWrap>
        {/** 사진선택 버튼 */}
        <DeliveryCompleteSelectPictureButton
          onCheck={handleCheckDeliveryComplete}
        />
        {/** 수취확인 버튼 */}
        <DeliveryCompleteSignatureButton
          onCheck={handleCheckDeliveryComplete}
        />
      </ButtonsWrap>
    </>
  );
};

const DeliveryCompleteSelectPictureButton = ({ onCheck }) => {
  const handleClick = () => {
    onCheck({ captureMethod: CAPTURE_METHOD.SELECT_PICTURE });
  };

  return (
    <ButtonSquare bgColor="bg-orange" onClick={handleClick}>
      사진선택
    </ButtonSquare>
  );
};

const DeliveryCompleteTakePictureButton = ({ onCheck }) => {
  const handleClick = () => {
    onCheck({ captureMethod: CAPTURE_METHOD.TAKE_PICTURE });
  };

  return (
    <ButtonSquare bgColor="bg-orange" onClick={handleClick}>
      사진촬영
    </ButtonSquare>
  );
};

const DeliveryCompleteSignatureButton = ({ onCheck }) => {
  const handleClick = () => {
    onCheck({ captureMethod: CAPTURE_METHOD.SIGNATURE });
  };

  return (
    <ButtonSquare bgColor="bg-orange" onClick={handleClick}>
      수취확인
    </ButtonSquare>
  );
};

// ============================================
// 서명/사진 보기 버튼
// ============================================

const DeliveryCompletedImageButton = ({ completedImage }) => {
  const { openModal } = useModalStore();

  const handleClickCompletedImage = () => {
    openModal(
      <DeliveryCompletedImageModal
        key="delivery-completed-image-modal"
        image={completedImage}
      />,
    );
  };

  return (
    <ButtonSquare onClick={handleClickCompletedImage}>
      서명/사진 보기
    </ButtonSquare>
  );
};

// ============================================
// 배송/분실/반송 완료 위치
// ============================================

const DeliveryCompletedLocationButton = ({
  bgColor = "bg-orange",
  flex = 1,
  completedLatitude,
  completedLongitude,
  label,
}) => {
  const handleClickCompletedLocation = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: POST_MESSAGES_TYPES.COMPLETED_LOCATION,
          latitude: completedLatitude,
          longitude: completedLongitude,
        }),
      );
    } else {
      window.alert(`${label} 보기에 실패했습니다. 다시 시도해주세요`);
    }
  };

  return (
    <button
      type="button"
      className={`${bgColor} text-white py-4 px-12 ${
        flex === 2 ? "flex-[2]" : "flex-1"
      }`}
      onClick={handleClickCompletedLocation}
    >
      {label}
    </button>
  );
};

// ============================================
// 배송 지연 버튼
// ============================================

const DeliveryDelayButton = ({ delivery, onAfterSubmit, isPickup = false }) => {
  const { openModal } = useModalStore();

  const handleClickDelay = () => {
    openModal(
      <DeliveryDelayModal
        key="delivery-delay-modal"
        delivery={delivery}
        onAfterSubmit={onAfterSubmit}
        isPickup={isPickup}
      />,
    );
  };

  return (
    <ButtonRound
      type="ORANGE"
      minWidth="min-w-[80px]"
      onClick={handleClickDelay}
    >
      {delivery.recentDelayedDelivery
        ? (() => {
            const { reason, createdAt } = delivery.recentDelayedDelivery;
            const shortReason = reason
              ? isPickup
                ? renderPickupDelayReasonShortText(reason)
                : renderDelayReasonShortText(reason)
              : null;
            const time = formatDate(createdAt, "HH:mm");
            return shortReason ? `${shortReason} ${time}` : time;
          })()
        : isPickup
          ? "수거지연"
          : "배송지연"}
      {delivery.isRequiredDelayMessage && "(!)"}
    </ButtonRound>
  );
};

// ============================================
// 위치보기 버튼
// ============================================

const DeliveryLocationButton = ({ type }) => {
  const handleClickLocation = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: POST_MESSAGES_TYPES.MAP,
          deliveryType: type,
        }),
      );
    } else {
      window.alert("위치보기에 실패했습니다. 다시 시도해주세요");
    }
  };

  return (
    <ButtonSquare bgColor="bg-mint" onClick={handleClickLocation}>
      위치보기
    </ButtonSquare>
  );
};

// ============================================
// 분실 완료 버튼
// ============================================

const DeliveryLostButton = ({ delivery, onAfterSubmit }) => {
  const { openModal } = useModalStore();

  const handleClickLost = () => {
    openModal(
      <DeliveryLostModal
        key="delivery-lost-modal"
        delivery={delivery}
        onAfterSubmit={onAfterSubmit}
      />,
    );
  };

  return (
    <ButtonSquare bgColor="border" onClick={handleClickLost}>
      분실완료
    </ButtonSquare>
  );
};

// ============================================
// 배송 연기 버튼
// ============================================

const DeliveryPostponedButton = ({ delivery, onAfterSubmit }) => {
  const { ...actions } = useDeliveriesStore();
  const { ...loadingActions } = useLoadingStore();

  const handleClickPostponed = async () => {
    if (
      window.confirm(
        `${delivery.bookId} 건을 배송연기 상태로 변경하시겠습니까?`,
      )
    ) {
      try {
        loadingActions.startLoading();
        await actions.setPostponed({ bookId: delivery.bookId });

        sendUpdatedMessage();

        onAfterSubmit();
      } catch (e) {
        window.alert(`배송연기 처리에 실패했습니다. ${e.message}`);
        loadingActions.finishLoading();
      }
    }
  };

  return (
    <ButtonSquare bgColor="bg-orange" onClick={handleClickPostponed}>
      연기등록
    </ButtonSquare>
  );
};

// ============================================
// 배송 이미지 재업로드 버튼
// ============================================

const DeliveryReUploadImageComponent = ({ delivery }) => {
  const handleClickSelectPicture = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: CAPTURE_METHOD.SELECT_PICTURE,
          deliveryType: ACTION_TYPE.REUPLOAD,
        }),
      );
    } else {
      window.alert("앱과의 통신에 실패했습니다. 다시 시도해주세요");
    }
  };

  const handleClickTakePicture = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: CAPTURE_METHOD.TAKE_PICTURE,
          deliveryType: ACTION_TYPE.REUPLOAD,
        }),
      );
    } else {
      window.alert("앱과의 통신에 실패했습니다. 다시 시도해주세요");
    }
  };

  return (
    <ButtonsWrap>
      <ButtonSquare bgColor="bg-mint" onClick={handleClickTakePicture}>
        재촬영
      </ButtonSquare>
      <ButtonSquare bgColor="bg-mint" onClick={handleClickSelectPicture}>
        사진선택
      </ButtonSquare>
    </ButtonsWrap>
  );
};

// ============================================
// 경로찾기 버튼
// ============================================

const DeliveryRouteButton = ({ delivery, type }) => {
  const { openModal } = useModalStore();

  const handleClickRoute = () => {
    openModal(
      <DeliveryRouteModal
        key="delivery-route-modal"
        delivery={delivery}
        type={type}
      />,
    );
  };

  return (
    <ButtonSquare bgColor="bg-mint" onClick={handleClickRoute}>
      경로찾기
    </ButtonSquare>
  );
};

// ============================================
// 반송 완료 버튼
// ============================================

const DeliverySentBackButton = ({ delivery, onAfterSubmit }) => {
  const { openModal } = useModalStore();

  const handleClickSentBack = () => {
    openModal(
      <DeliverySentBackModal
        key="delivery-sentback-modal"
        delivery={delivery}
        onAfterSubmit={onAfterSubmit}
      />,
    );
  };

  return (
    <ButtonSquare bgColor="border" onClick={handleClickSentBack}>
      반송완료
    </ButtonSquare>
  );
};

// ============================================
// 실배송 버튼
// ============================================

const DeliveryStartButton = ({ delivery, onAfterSubmit }) => {
  const { ...actions } = useDeliveriesStore();
  const { ...loadingActions } = useLoadingStore();
  const { openModal } = useModalStore();

  const handleClickStart = async () => {
    if (
      window.confirm(
        `${delivery.bookId} 건을 배송출발 상태로 변경하시겠습니까?`,
      )
    ) {
      try {
        loadingActions.startLoading();
        const result = await actions.setStart({ bookId: delivery.bookId });

        sendUpdatedMessage();

        // 스캔세션 재조회(fetchRecentSessions)는 웹뷰에 스캔 기능이 없어 의도적으로 생략함
        onAfterSubmit();

        // 일부 처리 실패 안내
        if (result?.failedMessage) {
          openModal(
            <AlertModal
              key="alert-modal"
              title="처리 완료"
              message={`일부 문제가 발생했습니다.\n${result.failedMessage}`}
            />,
          );
        }
      } catch (e) {
        window.alert(`실배송 처리에 실패했습니다. ${e.message}`);
        loadingActions.finishLoading();
      }
    }
  };

  return (
    <ButtonSquare bgColor="bg-orange" onClick={handleClickStart}>
      실배송등록
    </ButtonSquare>
  );
};

// ============================================
// 파일 재업로드 버튼
// ============================================

const FailedFileReuploadButton = ({ delivery, type }) => {
  const handleClickFileReupload = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: CAPTURE_METHOD.SELECT_PICTURE,
          deliveryType: `failed-reupload-${type}`,
        }),
      );
    }
  };

  return (
    <button
      type="button"
      className="bg-orange text-white py-4 px-12 flex-1"
      onClick={handleClickFileReupload}
    >
      파일재업로드{type === "delivery" ? "(수동)" : ""}
    </button>
  );
};

// ============================================
// 파일 재업로드(자동) 버튼
// ============================================

const FailedFileReuploadAutoButton = ({ delivery }) => {
  const handleClickFileReupload = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: CAPTURE_METHOD.SELECT_PICTURE,
          deliveryType: `failed-reupload-delivery-auto`,
        }),
      );
    }
  };

  return (
    <button
      type="button"
      className="bg-orange text-white py-4 px-12 flex-1"
      onClick={handleClickFileReupload}
    >
      파일재업로드(자동)
    </button>
  );
};

// ============================================
// 현관 비밀번호 버튼
// ============================================

const FrontdoorPasswordButton = ({
  delivery,
  frontdoorPasswords,
  onAfterSubmit,
}) => {
  const { ...baseAddressesActions } = useBaseAddressesStore();
  const { ...loadingActions } = useLoadingStore();

  const [currentPassword, setCurrentPassword] = useState(null);
  const [currentPasswordIndex, setCurrentPasswordIndex] = useState(null);
  const [currentPasswordType, setCurrentPasswordType] = useState(
    PASSWORD_TYPES.USER,
  );

  useEffect(() => {
    if (delivery.frontdoorPassword) {
      setCurrentPassword(delivery.frontdoorPassword);
      setCurrentPasswordIndex(null);
      setCurrentPasswordType(PASSWORD_TYPES.USER);
    } else if (frontdoorPasswords.length > 0) {
      setCurrentPassword(frontdoorPasswords[0]);
      setCurrentPasswordIndex(0);
      setCurrentPasswordType(PASSWORD_TYPES.CHAINLOGIS);
    }
  }, [delivery, frontdoorPasswords]);

  const handleChangePassword = () => {
    if (frontdoorPasswords.length === 0) return;

    const hasUserPassword = !!delivery.frontdoorPassword;

    if (currentPasswordType === PASSWORD_TYPES.USER) {
      setCurrentPassword(frontdoorPasswords[0]);
      setCurrentPasswordIndex(0);
      setCurrentPasswordType(PASSWORD_TYPES.CHAINLOGIS);
    } else {
      if (currentPasswordIndex === frontdoorPasswords.length - 1) {
        if (hasUserPassword) {
          setCurrentPassword(delivery.frontdoorPassword);
          setCurrentPasswordIndex(null);
          setCurrentPasswordType(PASSWORD_TYPES.USER);
        } else {
          setCurrentPassword(frontdoorPasswords[0]);
          setCurrentPasswordIndex(0);
          setCurrentPasswordType(PASSWORD_TYPES.CHAINLOGIS);
        }
      } else {
        const nextIndex = currentPasswordIndex + 1;
        setCurrentPassword(frontdoorPasswords[nextIndex]);
        setCurrentPasswordIndex(nextIndex);
        setCurrentPasswordType(PASSWORD_TYPES.CHAINLOGIS);
      }
    }
  };

  const handleClickDeactivate = async () => {
    if (
      currentPasswordType === PASSWORD_TYPES.CHAINLOGIS &&
      currentPassword?.id
    ) {
      if (
        window.confirm(
          `공동현관비밀번호를 오류보고하시겠습니까?\n${currentPassword?.password}`,
        )
      ) {
        try {
          loadingActions.startLoading();

          await baseAddressesActions.deactivateFrontdoorPassword(
            currentPassword.id,
          );

          onAfterSubmit();
        } catch (e) {
          console.log(e);
          window.alert(`오류보고에 실패했습니다.\n에러메시지: ${e.message}`);
        } finally {
          loadingActions.finishLoading();
        }
      }
    }
  };

  return (
    <div className="rounded-4 w-full flex items-center justify-between overflow-hidden">
      <div
        className="bg-orange text-white text-20 text-center px-20 py-2 relative flex-1 transition-all duration-150 active:scale-[0.97] active:brightness-90 cursor-pointer"
        onClick={handleClickDeactivate}
        role="button"
      >
        <img
          src={` ${
            currentPasswordType === PASSWORD_TYPES.USER
              ? "/images/key-user.png"
              : "/images/key-corp.png"
          }`}
          alt=""
          className="absolute w-28 h-28 left-5 top-[50%] mt-[-14px]"
        />
        {currentPasswordType === PASSWORD_TYPES.USER
          ? currentPassword
          : currentPassword?.password}
      </div>
      <button
        className="bg-[#ce3300] h-34 px-5 py-2 w-60 flex justify-center items-center transition-all duration-150 active:scale-[0.9] active:brightness-90"
        type="button"
        onClick={handleChangePassword}
      >
        <img src="/images/key-change.png" alt="" className="w-24 h-24" />
      </button>
    </div>
  );
};

// ============================================
// 수거 완료 버튼(일반건)
// ============================================

const PickupButton = ({ delivery }) => {
  const { ...actions } = useDeliveriesStore();
  const { ...loadingActions } = useLoadingStore();
  const { location } = useLocationStore().state;
  const { openModal } = useModalStore();

  const handleClickPickup = async () => {
    if (delivery?.typeNormal && !!delivery?.spot) {
      try {
        loadingActions.startLoading();

        // 수거지의 동일 수거건 목록 조회
        const sameSpotPickups = await actions.fetchSameSpotPickups(
          delivery.bookId,
        );

        loadingActions.finishLoading();

        if (sameSpotPickups?.length > 1) {
          openModal(
            <ConfirmModal
              key="confirm-modal"
              message={`${delivery.spotName} 수거지에 총 ${sameSpotPickups.length}건이 수거지정되어 있습니다.\n모두 수거하시겠습니까?`}
              onSubmit={() => handleSubmitPickupBulk(sameSpotPickups)}
              onCancel={confirmPickup}
            />,
          );
        } else {
          confirmPickup();
        }
      } catch (e) {
        window.alert(`수거 건이 확인되지 않습니다. ${e.message}`);
        loadingActions.finishLoading();
      }
    } else {
      confirmPickup();
    }
  };

  const confirmPickup = () => {
    openModal(
      <ConfirmModal
        key="confirm-modal"
        message="해당 물건을 수거하시겠습니까?"
        onSubmit={handleSubmitPickup}
      />,
    );
  };

  const handleSubmitPickupBulk = async (sameSpotPickups) => {
    if (sameSpotPickups.length > 0) {
      try {
        loadingActions.startLoading();

        const data = {
          pickedupLatitude: location.latitude,
          pickedupLongitude: location.longitude,
        };

        const response = await Promise.allSettled(
          sameSpotPickups.map((bookId) => {
            return actions.setPickup({
              bookId,
              data,
            });
          }),
        );

        const success = response.filter((el) => el.status === "fulfilled");

        sendUpdatedMessage();

        openModal(
          <AlertModal
            key="alert-modal"
            message={`${sameSpotPickups.length}건 중 ${success.length}건이 수거처리되었습니다.`}
            onSubmit={goToPickups}
          />,
        );
      } catch (e) {
        window.alert(`수거 처리가 되지 않았습니다. ${e.message}`);
      }

      loadingActions.finishLoading();
    } else {
      window.alert("수거 내역이 없습니다.");
    }
  };

  const handleSubmitPickup = async () => {
    try {
      loadingActions.startLoading();

      const data = {
        pickedupLatitude: location.latitude,
        pickedupLongitude: location.longitude,
      };

      await actions.setPickup({ bookId: delivery.bookId, data });

      sendUpdatedMessage();

      goToPickups();
    } catch (e) {
      window.alert(`수거완료 처리에 실패했습니다. ${e.message}`);
    }

    loadingActions.finishLoading();
  };

  return (
    <ButtonSquare bgColor="bg-orange" onClick={handleClickPickup}>
      수거완료
    </ButtonSquare>
  );
};

// ============================================
// 수거 완료(픽반품) 컴포넌트
// ============================================

const PickupCompleteComponent = () => {
  const handleClickSelectPicture = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: CAPTURE_METHOD.SELECT_PICTURE,
          deliveryType: ACTION_TYPE.PICKUP,
        }),
      );
    } else {
      window.alert("앱과의 통신에 실패했습니다. 다시 시도해주세요");
    }
  };

  const handleClickTakePicture = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: CAPTURE_METHOD.TAKE_PICTURE,
          deliveryType: ACTION_TYPE.PICKUP,
        }),
      );
    } else {
      window.alert("앱과의 통신에 실패했습니다. 다시 시도해주세요");
    }
  };

  return (
    <ButtonsWrap>
      <ButtonSquare bgColor="bg-orange" onClick={handleClickSelectPicture}>
        사진선택
      </ButtonSquare>
      <ButtonSquare bgColor="bg-orange" onClick={handleClickTakePicture}>
        수거촬영
      </ButtonSquare>
    </ButtonsWrap>
  );
};

// ============================================
// 물품정보 컴포넌트
// ============================================

const ProductNameComponent = ({ delivery }) => {
  const { openModal } = useModalStore();

  const handleClickProductName = () => {
    openModal(
      <AlertModal key="alert-modal" message={delivery.totalProductName} />,
    );
  };

  return (
    <InfoRow>
      <InfoLabel>물품정보</InfoLabel>
      <InfoValue>
        <div className="line-clamp-1" onClick={handleClickProductName}>
          {delivery.totalProductName}
        </div>
      </InfoValue>
    </InfoRow>
  );
};

// ============================================
// 재배차 버튼
// ============================================

const ReallocationButton = ({ delivery }) => {
  const { openModal } = useModalStore();

  const handleClickReallocation = () => {
    if (delivery.reallocatedRiderId) {
      sendToastMessage("관제팀 확인 중");
    } else {
      openModal(
        <ReallocationRidersModal
          key="reallocation-riders-modal"
          delivery={delivery}
        />,
      );
    }
  };

  return (
    <button
      type="button"
      className={`${
        delivery.reallocatedRiderId ? " text-gray-a" : " text-orange"
      } bg-white py-2 px-12 rounded-full min-w-[80px] flex justify-center items-center text-16`}
      onClick={handleClickReallocation}
    >
      <img
        src={`/images/rearrange${
          delivery.reallocatedRiderId ? "" : "-red"
        }.png`}
        alt=""
        className="w-24"
      />
      재배차
    </button>
  );
};

// ============================================
// 번역 오류 보고
// ============================================

const ReportTranslationErrorButton = ({ delivery }) => {
  const { ...actions } = useDeliveriesStore();
  const { ...loadingActions } = useLoadingStore();

  const handleClickReportTranslationError = async () => {
    if (window.confirm("번역 오류를 보고하시겠습니까?")) {
      try {
        loadingActions.startLoading();

        const data = {
          bookId: delivery.bookId,
          translatedPlatform: "openai",
          address: delivery.translatedResultOpenai?.translatedText,
          addressEng: delivery.translatedResultOpenai?.originText,
        };

        await actions.reportTranslationError({
          data,
        });

        sendToastMessage("번역 오류를 보고했습니다.");
      } catch (e) {
        window.alert(`번역 오류 보고에 실패했습니다. ${e.message}`);
      } finally {
        loadingActions.finishLoading();
      }
    }
  };

  return (
    <ButtonSquare
      bgColor="bg-black"
      onClick={handleClickReportTranslationError}
    >
      번역오류보고
    </ButtonSquare>
  );
};

// ============================================
// 위치 등록 버튼
// ============================================

const ResetAddressButton = ({ delivery, type = "receiver", onAfterSubmit }) => {
  const { openModal } = useModalStore();

  const handleClickResetAddress = () => {
    openModal(
      <UpdateAddressModal
        key="update-address-modal"
        delivery={delivery}
        type={type}
        updateType="reset"
        onAfterSubmit={onAfterSubmit}
      />,
    );
  };

  return (
    <ButtonSquare onClick={handleClickResetAddress}>
      위치 등록 (필수)
    </ButtonSquare>
  );
};

// ============================================
// 문자 발송 버튼
// ============================================

const SendMessageButton = ({ delivery, mobile, type, active }) => {
  const { openModal } = useModalStore();
  const { user } = useAuthStore().state;

  const handleClickMessage = () => {
    if (type === "pickupReturn") {
      openModal(
        <ConfirmModal
          key="confirm-modal"
          message="반품수거 안내 및 차량등록 요청 문자를 발송하시겠습니까?"
          onSubmit={sendMessageForPickup}
        />,
      );
    } else {
      const hasVehicle = user?.vehicles?.length > 0;
      const vehicleNumbers =
        user?.vehicles?.map((vehicle) => vehicle.vehicleNumber).join(" / ") ||
        "";

      const messageForAddress = `[두발히어로]${delivery.renderReceiverName}님, ${
        delivery.bookId
      }${delivery.orderIdFromCorp ? `/${delivery.orderIdFromCorp}` : ""} ${
        delivery.senderName
      } 배송 주소 오류로 정확한 주소 문자로 바로 회신 부탁드립니다.\n★미회신 시 반송 주의`;

      const messageForVehicle = `[두발히어로]${delivery.renderReceiverName}님, ${
        delivery.bookId
      }${delivery.orderIdFromCorp ? `/${delivery.orderIdFromCorp}` : ""} ${
        delivery.senderName
      } 당일택배 배송을 위해 아래 차량번호를 바로 등록 부탁드립니다. 미등록 시 배송이 지연될 수 있으니 양해 부탁드립니다.\n-차량번호: ${vehicleNumbers}`;

      openModal(
        <SendMessageModal
          key="send-message-modal"
          mobile={mobile}
          messageForAddress={messageForAddress}
          messageForVehicle={messageForVehicle}
          hasVehicle={hasVehicle}
          bookId={delivery.bookId}
        />,
      );
    }
  };

  const sendMessageForPickup = () => {
    const vehicleSection =
      user?.vehicles?.length > 0
        ? `차량 등록이 필요한 경우 아래 차량번호를 꼭 등록해주십시오.\n-차량번호: ${user.vehicles
            .map((vehicle) => vehicle.vehicleNumber)
            .join(" / ")}\n\n`
        : "";

    const message = `[두발히어로 반품수거 안내] 고객님 오늘 ${
      delivery.renderReceiverName
    } 물품의 반품 수거를 위해 방문 예정입니다. 수거할 물품박스에 구매처 브랜드명과, 두발히어로를 크게 기재해 주시고, 문앞이나 수거 가능한 보관 위치에 물품을 보관 후 문자로 꼭 회신 부탁드립니다.\n공동현관 출입 시 비밀번호가 필요하다면 회신 문자에 포함 부탁드리겠습니다.\n${vehicleSection}■반품/수거 접수처 : ${
      delivery.renderReceiverName
    }\n■접수번호 : ${delivery.bookId}\n■방문주소 : ${
      delivery.senderAddressRoad || ""
    }`;

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: POST_MESSAGES_TYPES.SEND_MESSAGE_FOR_DIRECT,
          mobile,
          message,
          bookId: delivery.bookId,
        }),
      );
    } else {
      window.alert("문자 발송에 실패했습니다. 다시 시도해주세요");
    }
  };

  return (
    <button
      type="button"
      className={`${
        active
          ? "bg-orange text-white border-1 border-orange"
          : "bg-white text-gray-a border-gray-d"
      } py-2 px-12 border-1 rounded-10 ml-5 text-16`}
      onClick={handleClickMessage}
    >
      문자
    </button>
  );
};

// ============================================
// 주소 요청 문자 발송 버튼
// ============================================

const SendMessageForRequestAddressButton = ({ delivery }) => {
  const { openModal } = useModalStore();

  const handleClickMessage = () => {
    openModal(
      <ConfirmModal
        key="confirm-modal"
        title={`${delivery.receiverMobile} 주소 요청 문자`}
        message={`[두발히어로]${delivery.renderReceiverName}님, ${delivery.bookId}${
          delivery.orderIdFromCorp ? `/${delivery.orderIdFromCorp}` : ""
        } ${
          delivery.senderName
        } 배송 주소 오류로 정확한 주소 문자로 바로 회신 부탁드립니다.\n★미회신 시 반송 주의`}
        onSubmit={sendMessageForAddress}
      />,
    );
  };

  const sendMessageForAddress = () => {
    const message = `[두발히어로]${delivery.renderReceiverName}님, ${delivery.bookId}${
      delivery.orderIdFromCorp ? `/${delivery.orderIdFromCorp}` : ""
    } ${
      delivery.senderName
    } 배송 주소 오류로 정확한 주소 문자로 바로 회신 부탁드립니다.\n★미회신 시 반송 주의`;

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: POST_MESSAGES_TYPES.SEND_MESSAGE_FOR_DIRECT,
          mobile: delivery.receiverMobile,
          message,
          bookId: delivery.bookId,
        }),
      );
    } else {
      window.alert("문자 발송에 실패했습니다. 다시 시도해주세요");
    }
  };

  return (
    <ButtonSquare bgColor="bg-black" onClick={handleClickMessage}>
      주소 요청 문자 발송
    </ButtonSquare>
  );
};

// ============================================
// 주소 수정 버튼
// ============================================

const UpdateAddressButton = ({
  delivery,
  type = "receiver",
  onAfterSubmit,
}) => {
  const { openModal } = useModalStore();

  const handleClickUpdateAddress = () => {
    openModal(
      <UpdateAddressModal
        key="update-address-modal"
        delivery={delivery}
        type={type}
        updateType="update"
        onAfterSubmit={onAfterSubmit}
      />,
    );
  };

  return (
    <ButtonSquare bgColor="bg-orange" onClick={handleClickUpdateAddress}>
      주소 수정
    </ButtonSquare>
  );
};

// ============================================
// 주소 등록 버튼(번역 텍스트)
// ============================================

const UpdateAddressForTranslationButton = ({
  address,
  addressType,
  bookId,
  label,
  onAfterSubmit,
  translatedPlatform,
}) => {
  const { ...loadingActions } = useLoadingStore();
  const { ...actions } = useDeliveriesStore();

  const handleClickUpdateAddress = async () => {
    if (window.confirm(`(${label}) [${address}]로 주소를 등록하시겠습니까?`)) {
      try {
        loadingActions.startLoading();

        const data = {
          translatedPlatform,
          addressType,
        };

        await actions.updateAddressFromEtc4({
          bookId,
          data,
        });

        onAfterSubmit();
      } catch (e) {
        window.alert(`주소 등록에 실패했습니다. \n에러메시지: ${e.message}`);
      } finally {
        loadingActions.finishLoading();
      }
    }
  };

  return (
    <ButtonSquare bgColor="bg-black" onClick={handleClickUpdateAddress}>
      {label} 주소 등록
    </ButtonSquare>
  );
};

// ============================================
// 배송 완료 위치 수정 버튼
// ============================================

const UpdateDeliveryCompletedLocationButton = ({ delivery, onAfterSubmit }) => {
  const { ...actions } = useDeliveriesStore();
  const { ...loadingActions } = useLoadingStore();
  const { openModal } = useModalStore();

  const onSubmit = async ({ code, text, postboxPassword }) => {
    try {
      loadingActions.startLoading();

      let sendedCompletedSms = false;

      if (delivery.receiverSmsMobile || delivery.receiverMobile) {
        sendedCompletedSms = true;
      }

      // etc4에 배송 위치 저장
      const { urlToCheckCompletedDelivery } = await actions.setDeliveryLocation(
        {
          bookId: delivery.bookId,
          data: {
            completedLocation: `${text}${
              code === COMPLETED_LOCATION_POSTBOX && postboxPassword
                ? `(${postboxPassword})`
                : ""
            }`,
            completedLocationCode: code,
            sendedCompletedSms,
            alimtalk: false,
          },
        },
      );

      if (sendedCompletedSms) {
        let message = `[두발히어로]배송완료했습니다(${text}${
          code === COMPLETED_LOCATION_POSTBOX && postboxPassword
            ? `(${postboxPassword})`
            : ""
        })\n완료사진 ${urlToCheckCompletedDelivery}`;

        if (!urlToCheckCompletedDelivery) {
          message = `[두발히어로]배송완료했습니다(${text}${
            code === COMPLETED_LOCATION_POSTBOX && postboxPassword
              ? `(${postboxPassword})`
              : ""
          })`;
        }

        sendSmsMessageForDirect({
          mobile: delivery.receiverSmsMobile || delivery.receiverMobile,
          message,
          bookId: delivery.bookId,
        });

        sendToastMessage("배송완료 메시지를 발송했습니다.");
      } else {
        sendToastMessage("배송 완료 위치를 저장했습니다.");
      }

      onAfterSubmit();
    } catch (e) {
      window.alert(`배송건 토큰 발급에 실패했습니다. ${e.message}`);
    }
  };

  const handleClickUpdateLocation = () => {
    openModal(
      <UpdateCompletedLocationModal
        key="update-completed-location-modal"
        cancelable={true}
        onSubmit={onSubmit}
      />,
    );
  };

  return (
    <button
      type="button"
      className={`${
        delivery.sendedCompletedSms
          ? "bg-mint border-mint"
          : "bg-orange border-orange"
      } text-white border-1  py-2 px-12 rounded-10 ml-5`}
      onClick={handleClickUpdateLocation}
    >
      위치
    </button>
  );
};

function Delivery() {
  const params = new URLSearchParams(window.location.search);
  const bookId = params.get("bookId");
  const _isFailedFile = params.get("isFailedFile");
  const isFailedFile = _isFailedFile ? JSON.parse(_isFailedFile) : false;
  // const listStatus = params.get("listStatus");

  const {
    baseAddressesByBuildingDongNumber,
    baseAddressesBookId,
    frontdoorPasswordsByAddress,
    isCollectionRestricted,
  } = useBaseAddressesStore().state;
  const { ...baseAddressesActions } = useBaseAddressesStore();

  const {
    hasCsesByBookId,
    hasCsesByBookIdNonChecked,
    csesUserByBookId,
    csesChainlogisByBookId,
  } = useCsesStore().state;
  const { ...csesActions } = useCsesStore();

  const { ...authActions } = useAuthStore();

  const { delivery } = useDeliveriesStore().state;
  const { ...actions } = useDeliveriesStore();

  // postMessage 리스너(마운트 시 1회만 등록)가 항상 최신 delivery를 참조하도록
  // stale closure 방지용 ref (deps 재등록 없이 값만 동기화)
  const deliveryRef = useRef(delivery);

  useEffect(() => {
    deliveryRef.current = delivery;
  }, [delivery]);

  // RN이 화면 마운트 시 1회 push하는 배차된 전체 배송건 목록(배송순서 체크용).
  // 렌더링에 사용하지 않는 캐시값이라 ref로 보관 (deliveryRef와 동일한 이유)
  const deliveriesAllocatedRef = useRef([]);

  const { ...loadingActions } = useLoadingStore();
  const { ...locationActions } = useLocationStore();

  const { openModal, closeModal } = useModalStore();

  useEffect(() => {
    if (bookId) {
      fetchById();
    }
  }, [bookId]);

  // 차량정보(vehicles) 등 라이더 본인 정보 갱신 - 화면 핵심 기능과 무관한
  // 부가 정보이므로 실패해도 화면 전체에 영향 없도록 조용히 무시
  useEffect(() => {
    (async () => {
      try {
        await authActions.fetchMe();
      } catch (e) {
        // noop
      }
    })();
  }, []);

  useEffect(() => {
    if (delivery?.bookId === bookId) {
      fetchBaseAddressById();
      fetchCsesById();
      fetchFrontdoorPasswords();
    }
  }, [delivery, bookId]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      document.addEventListener("message", handleWebviewMessage);
    }, 200);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener("message", handleWebviewMessage);
    };
  }, []);

  // ============================================
  // 웹뷰 메시지 처리
  // ============================================

  const handleWebviewMessage = (e) => {
    if (!e.data) return;

    const data = JSON.parse(e.data);

    // location 업데이트
    if (data.type === "location" && data.location) {
      locationActions.updateLocation(data.location);
      return;
    }

    // 사용자가 RN 헤더에서 폰트 크기를 변경할 때마다 실시간으로 전달됨.
    // medium/large/xlarge 외의 값이 오면 무시(방어 코드).
    if (data.type === "size" && data.size) {
      applySizeClass(data.size);
      return;
    }

    // 배차된 전체 배송건 목록 저장(배송순서 체크용, 화면 마운트 시 1회 push됨)
    if (data.type === "deliveriesAllocated") {
      deliveriesAllocatedRef.current = data.data || [];
      return;
    }

    // 이미지 캡처 관련 메시지 처리
    const isImageMessage =
      data.type === CAPTURE_METHOD.SELECT_PICTURE ||
      data.type === CAPTURE_METHOD.TAKE_PICTURE ||
      data.type === CAPTURE_METHOD.SIGNATURE;

    if (isImageMessage && data.deliveryType) {
      handleImageUploadMessage({
        captureMethod: data.type,
        action: data.deliveryType,
        imageUrl: data.imageUrl,
        location: data.location,
        bookIds: data.bookIds,
      });
    }
  };

  // 이미지 업로드 메시지 처리
  const handleImageUploadMessage = ({
    captureMethod,
    action,
    imageUrl,
    location,
    bookIds,
  }) => {
    switch (action) {
      case ACTION_TYPE.PICKUP:
        if (imageUrl) {
          handleSubmitPickup({ captureMethod, imageUrl, location });
        } else {
          window.alert("이미지 촬영에 문제가 발생했습니다. 다시 시도해주세요.");
        }
        break;

      case ACTION_TYPE.DELIVERY:
        if (imageUrl) {
          handleUpdateDeliveryLocation({
            captureMethod,
            imageUrl,
            location,
            bookIds,
          });
        } else {
          window.alert("이미지 촬영에 문제가 발생했습니다. 다시 시도해주세요.");
        }
        break;

      case ACTION_TYPE.REUPLOAD:
        if (imageUrl) {
          handleSubmitReuploadImage({ imageUrl });
        } else {
          window.alert("이미지 촬영에 문제가 발생했습니다. 다시 시도해주세요.");
        }
        break;

      case ACTION_TYPE.CANCEL_PICKUP:
        if (imageUrl) {
          openModal(
            <CancelDeliveryPickupReturnModal
              key="cancel-delivery-pickup-return-modal"
              delivery={deliveryRef.current}
              pickupCanceledImage={imageUrl}
              location={location}
            />,
          );
        } else {
          window.alert("이미지 촬영에 문제가 발생했습니다. 다시 시도해주세요.");
        }
        break;

      case "failed-reupload-pickup":
      case "failed-reupload-delivery":
      case "failed-reupload-delivery-auto":
        if (imageUrl) {
          handleSubmitFailedReupload({ action, imageUrl });
        } else {
          window.alert("이미지 촬영에 문제가 발생했습니다. 다시 시도해주세요.");
        }
        break;

      default:
        console.warn(`Unknown action type: ${action}`);
    }
  };

  // ============================================
  // 데이터 조회 함수들
  // ============================================

  const fetchById = async () => {
    try {
      loadingActions.startLoading();
      await actions.fetchById(bookId);
    } catch (e) {
      window.alert(`배송 조회에 실패했습니다. ${e.message}`);
    }
    loadingActions.finishLoading();
  };

  const fetchBaseAddressById = async () => {
    try {
      await baseAddressesActions.fetchAllById({
        bookId: delivery.bookId,
        buildingDongNumber: delivery.typeNormal
          ? delivery.receiverBuildingDongNumber
          : delivery.senderBuildingDongNumber,
        addressRoad: delivery.typeNormal
          ? delivery.receiverAddressRoad
          : delivery.senderAddressRoad,
        address: delivery.typeNormal
          ? delivery.receiverAddress
          : delivery.senderAddress,
      });
    } catch (e) {
      // window.alert(`주소 정보 조회에 실패했습니다. ${e.message}`);
    }
  };

  const fetchCsesById = async () => {
    try {
      await csesActions.fetchCsesById({
        bookId: delivery.bookId,
        receiptDate: delivery.receiptDate,
      });
    } catch (e) {
      window.alert(`CS 목록 조회에 실패했습니다. ${e.message}`);
    }
  };

  const fetchFrontdoorPasswords = async () => {
    try {
      let query = {};

      if (delivery.typeNormal) {
        query.addressRoad = delivery.receiverAddressRoad;
        if (delivery.receiverBuilding) {
          query.building = delivery.receiverBuilding;
        }
        if (delivery.receiverBuildingDongNumber) {
          query.buildingDongNumber =
            delivery.receiverBuildingDongNumber.replace("동", "");
        }
        if (delivery.receiverBuildingHouseNumber) {
          query.buildingHouseNumber =
            delivery.receiverBuildingHouseNumber.replace("호", "");
        }
      } else {
        query.addressRoad = delivery.senderAddressRoad;
        if (delivery.senderBuilding) {
          query.building = delivery.senderBuilding;
        }
        if (delivery.senderBuildingDongNumber) {
          query.buildingDongNumber = delivery.senderBuildingDongNumber.replace(
            "동",
            "",
          );
        }
        if (delivery.senderBuildingHouseNumber) {
          query.buildingHouseNumber =
            delivery.senderBuildingHouseNumber.replace("호", "");
        }
      }

      // 수거제한 주소 확인 (원본 fetchFrontdoorPasswords와 동일 시점)
      const collectionRestricted =
        await baseAddressesActions.fetchCollectionRestrictedByAddress({
          ...query,
          page: 1,
          pageSize: 10,
        });

      if (collectionRestricted.length > 0) {
        return;
      }

      await baseAddressesActions.fetchFrontdoorPasswordsByAddress(query);
    } catch (e) {
      // window.alert(`현관 비밀번호 목록 조회에 실패했습니다. ${e.message}`);
    }
  };

  const handleAfterSubmitFrontdoorPasswords = () => {
    fetchFrontdoorPasswords();
    fetchById();
  };

  // ============================================
  // 수거 완료 처리
  // ============================================

  const handleSubmitPickup = async ({ imageUrl, location }) => {
    try {
      loadingActions.startLoading();

      await actions.setPickup({
        bookId,
        data: {
          image: imageUrl,
          pickedupLatitude: location.latitude,
          pickedupLongitude: location.longitude,
        },
      });

      sendUpdatedMessage();

      goToPickups();
    } catch (e) {
      window.alert(`수거완료 처리에 실패했습니다. ${e.message}`);
    }

    loadingActions.finishLoading();
  };

  // ============================================
  // 배송 완료 처리
  // ============================================

  const handleUpdateDeliveryLocation = ({
    captureMethod,
    imageUrl,
    location,
    bookIds,
  }) => {
    openModal(
      <UpdateCompletedLocationModal
        key="update-completed-location-modal"
        cancelable={false}
        bookIds={bookIds}
        captureMethod={captureMethod}
        imageUrl={imageUrl}
        location={location}
        onSubmit={handleSubmitDelivery}
      />,
    );
  };

  const handleSubmitDelivery = async ({
    captureMethod,
    imageUrl,
    location,
    bookIds,
    completedLocation,
    completedLocationCode,
  }) => {
    try {
      loadingActions.startLoading();

      let data = {
        alimtalk: true,
        reasonType: completedLocationCode,
        deliveryDoneLatitude: location.latitude,
        deliveryDoneLongitude: location.longitude,
      };

      // 서명과 사진 구분
      const isSignature = captureMethod === CAPTURE_METHOD.SIGNATURE;

      if (isSignature) {
        data.signImageLocation = imageUrl;
      } else {
        data.notReceivedImageLocation = imageUrl;
      }

      // 단일건 완료 처리 후에만 배송순서 체크 (원본과 동일하게 다건 처리 시는 생략)
      let shouldCheckSequence = false;

      // 복수 배송건 처리
      if (bookIds?.length > 0) {
        let _bookIds = [bookId, ...bookIds];

        const response = await Promise.allSettled(
          _bookIds.map((b) => {
            return actions.setDelivered({
              bookId: b,
              data,
            });
          }),
        );

        const failed = response.filter((el) => el.status !== "fulfilled");

        if (failed.length > 0) {
          window.alert(
            `배송완료 처리에 실패했습니다. ${failed[0].reason.message}`,
          );
          loadingActions.finishLoading();
          return;
        }

        // 배송 완료 위치 저장 (첫 건만 code/SMS 처리, 2번째 이후는 위치 텍스트만)
        for (let i = 0; i < _bookIds.length; i++) {
          await updateDeliveryCompletedLocation({
            bookId: _bookIds[i],
            alimtalk: true,
            completedLocation,
            completedLocationCode,
            multi: true,
            isFirst: i === 0,
          });

          if (_bookIds.length - 1 === i) {
            await fetchById();
          }
        }
      } else {
        // 단일 배송건 처리
        await actions.setDelivered({
          bookId,
          data,
        });

        // 배송 위치 저장
        await updateDeliveryCompletedLocation({
          bookId,
          alimtalk: true,
          completedLocation,
          completedLocationCode,
        });

        await fetchById();

        shouldCheckSequence = true;
      }

      sendUpdatedMessage();

      closeModal();

      if (!location?.latitude || !location?.longitude) {
        window.alert(
          "현재 기사님의 위치를 알 수 없습니다. 휴대폰의 위치 설정에서 [위치 사용] 이 활성화되어 있는지 확인해주십시오. 위치 사용을 활성화하지 않고 배송완료 시 완료 위치값이 저장되지 않아 추후 고객과 분쟁이 발생할 수 있습니다.",
        );
      }

      // openModal로 안내 모달을 띄우므로 closeModal() 이후에 호출해야
      // 방금 닫은 완료처리 모달이 이 안내 모달을 덮어 닫아버리지 않음
      if (shouldCheckSequence) {
        await checkSequenceAfterDelivery(bookId);
      }
    } catch (e) {
      window.alert(`배송완료 처리에 실패했습니다. ${e.message}`);
      loadingActions.finishLoading();
    }
  };

  // 배송순서 체크: 지정된 순서보다 먼저 완료한 배송건이 있으면 안내
  const checkSequenceAfterDelivery = async (completedBookId) => {
    try {
      const response = await actions.fetchRouteByBookId(completedBookId);
      if (!response?.stops?.length) return;

      const completedStop = response.stops.find(
        (s) => s.bookId === completedBookId,
      );
      if (!completedStop) return;

      const outOfOrderStops = response.stops.filter(
        (s) =>
          s.sequence < completedStop.sequence && s.stopStatus === "PENDING",
      );

      if (outOfOrderStops.length === 0) return;

      const activeDeliveryBookIds = new Set(
        deliveriesAllocatedRef.current
          .filter((d) => d.status === RN_STATUS_DELIVERY_STARTED)
          .map((d) => d.bookId),
      );

      const realOutOfOrderStops = outOfOrderStops.filter((s) =>
        activeDeliveryBookIds.has(s.bookId),
      );

      if (realOutOfOrderStops.length > 0) {
        const pendingSequences = realOutOfOrderStops
          .sort((a, b) => a.sequence - b.sequence)
          .map((s) => `[${s.sequence}]`)
          .join(" ");

        openModal(
          <AlertModal
            key="check-sequence-alert-modal"
            message={`지정된 배송순서와 다르게 배송했습니다. 지정한 배송순서 대로 배송해주십시오.\n-완료한 순서 [${completedStop.sequence}]\n-미완료 순서 ${pendingSequences}`}
          />,
        );
      }
    } catch (e) {
      console.error("배송순서 체크 실패", e);
    }
  };

  // 배송 완료 위치 저장
  const updateDeliveryCompletedLocation = async ({
    bookId,
    alimtalk,
    completedLocation,
    completedLocationCode,
    multi = false,
    isFirst = false,
  }) => {
    try {
      // 다건 중 2번째 이후 건: 완료위치 텍스트만 저장, code/SMS 없음
      if (multi && !isFirst) {
        const response = await actions.setDeliveryLocation({
          bookId,
          data: {
            completedLocation,
            sendedCompletedSms: false,
            alimtalk,
          },
        });

        sendToastMessage("배송 완료 위치를 저장했습니다.");
        return response;
      }

      // 단건 또는 다건 첫 건: 완료위치 종류와 무관하게 code 항상 저장 + SMS 조건 판단
      const sendedCompletedSms = shouldSendSms(
        deliveryRef.current,
        completedLocationCode,
      );

      const { urlToCheckCompletedDelivery } = await actions.setDeliveryLocation(
        {
          bookId,
          data: {
            completedLocation,
            completedLocationCode,
            sendedCompletedSms,
            alimtalk,
          },
        },
      );

      if (sendedCompletedSms) {
        let message = `[두발히어로]배송완료했습니다(${completedLocation})\n완료사진 ${urlToCheckCompletedDelivery}`;

        if (!urlToCheckCompletedDelivery) {
          message = `[두발히어로]배송완료했습니다(${completedLocation})`;
        }

        sendSmsMessageForDirect({
          mobile:
            deliveryRef.current?.receiverSmsMobile ||
            deliveryRef.current?.receiverMobile,
          message,
          bookId,
        });
      } else {
        sendToastMessage("배송 완료 위치를 저장했습니다.");
      }

      return { urlToCheckCompletedDelivery };
    } catch (e) {
      sendToastMessage("배송 완료 위치 저장에 실패했습니다.");
    }
  };

  // 배송 완료 이미지 재업로드
  const handleSubmitReuploadImage = async ({ imageUrl }) => {
    try {
      loadingActions.startLoading();

      await actions.updateImages({
        bookId,
        data: {
          imageUrl,
          type: "notReceivedImageLocation",
        },
      });

      await fetchById();

      sendToastMessage("배송 완료 이미지 재업로드에 성공했습니다.");
    } catch (e) {
      window.alert(`배송 완료 이미지 재업로드에 실패했습니다. ${e.message}`);
      loadingActions.finishLoading();
    }
  };

  // 실패목록 파일 재업로드 처리 (수동/자동 공통)
  // RN 래퍼(uploadFileForFailedReupload)가 S3 재업로드 + 실패목록 제거까지
  // 네이티브에서 처리 후 imageUrl만 전달하므로, 웹뷰는 해당 필드만 갱신하면 됨
  const handleSubmitFailedReupload = async ({ action, imageUrl }) => {
    if (!deliveryRef.current) return;

    try {
      loadingActions.startLoading();

      let type;
      if (action === "failed-reupload-pickup") {
        type = "image";
      } else {
        // failed-reupload-delivery / failed-reupload-delivery-auto
        type = deliveryRef.current?.signImageLocation
          ? "signImageLocation"
          : "notReceivedImageLocation";
      }

      await actions.updateImages({
        bookId,
        data: {
          imageUrl,
          type,
        },
      });

      await fetchById();

      sendToastMessage("파일 업로드에 성공했습니다.");
    } catch (e) {
      window.alert(`파일 업로드에 실패했습니다. ${e.message}`);
      loadingActions.finishLoading();
    }
  };

  const handleClickReceiverName = () => {
    openModal(
      <AlertModal
        key="receiver-name-modal"
        message={delivery.receiverName}
        onClose={() => {
          closeModal();
        }}
      />,
    );
  };

  if (!delivery) {
    return <></>;
  }

  return (
    <div>
      {/** 주요 정보 */}
      <div className="bg-[#ffff00] p-10 grid gap-0">
        {(delivery.statusPickup || delivery.statusDelivery) && (
          <div className="flex justify-end items-center gap-5 mb-10">
            {/** 편의점 바코드 */}
            {delivery.isConvenienceBarcode && (
              <ConvenienceBarcodeButton delivery={delivery} />
            )}

            {/** 재배차 처리 */}
            <ReallocationButton delivery={delivery} />

            {/** 픽/반품 수거건 취소 */}
            {delivery.statusPickup && delivery.typePickupReturn && (
              <CancelDeliveryPickupReturnButton />
            )}
            {/** 픽/반품 수거지연 처리 */}
            {delivery.statusPickup && delivery.typePickupReturn && (
              <DeliveryDelayButton
                delivery={delivery}
                onAfterSubmit={fetchById}
                isPickup
              />
            )}
            {/** 배송 지연 처리 */}
            {delivery.statusDelivery && (
              <DeliveryDelayButton
                delivery={delivery}
                onAfterSubmit={fetchById}
              />
            )}
          </div>
        )}

        {delivery.orderIdFromCorp && (
          <InfoRow>
            <InfoLabel>사측번호</InfoLabel>
            <InfoValue>{delivery.orderIdFromCorp}</InfoValue>
          </InfoRow>
        )}

        {(delivery.renderRequestedDeliveryTime ||
          delivery.renderLimitedDeliveryAt) && (
          <InfoRow>
            <InfoLabel>확인사항</InfoLabel>
            <InfoValue>
              {delivery.renderRequestedDeliveryTime ||
                delivery.renderLimitedDeliveryAt}
            </InfoValue>
          </InfoRow>
        )}

        {delivery.renderRequestedDeliveryTimeForOliveQuick && (
          <InfoRow>
            <InfoLabel>도착요청</InfoLabel>
            <InfoValue>
              {delivery.renderRequestedDeliveryTimeForOliveQuick}
            </InfoValue>
          </InfoRow>
        )}

        {baseAddressesBookId === bookId &&
          baseAddressesByBuildingDongNumber?.length > 0 &&
          !isCollectionRestricted && (
            <InfoRow>
              <InfoLabel>주소정보</InfoLabel>
              <InfoValue>{baseAddressesByBuildingDongNumber[0].memo}</InfoValue>
            </InfoRow>
          )}

        {delivery.isHighPrice && (
          <InfoRow>
            <InfoLabel>{!delivery.isLimitedHours && "확인사항"}</InfoLabel>
            <InfoValue>고객연락 및 확인 필수</InfoValue>
          </InfoRow>
        )}

        <InfoRow>
          {(delivery.statusPickupCompleted ||
            delivery.statusDeliveryCompletedOrSentBackOrLost) && (
            <InfoLabel>고객요청</InfoLabel>
          )}
          <InfoValue
            large={
              !delivery.statusPickupCompleted &&
              !delivery.statusDeliveryCompletedOrSentBackOrLost
            }
          >
            {delivery.statusPickupCompleted ||
            delivery.statusDeliveryCompletedOrSentBackOrLost
              ? ""
              : "[요청주의] "}
            {delivery.memo || "없음"}
          </InfoValue>
        </InfoRow>

        <InfoRow>
          {(delivery.statusPickupCompleted ||
            delivery.statusDeliveryCompletedOrSentBackOrLost) && (
            <InfoLabel>아파트동</InfoLabel>
          )}
          <InfoValue
            large={
              !delivery.statusPickupCompleted &&
              !delivery.statusDeliveryCompletedOrSentBackOrLost
            }
          >
            {delivery.typeNormal
              ? delivery.receiverBuildingDongHouseNumber || "없음"
              : delivery.senderBuildingDongHouseNumber || "없음"}
            ({delivery.renderAddressApiType})
          </InfoValue>
        </InfoRow>

        {/** 주소지 */}
        {delivery.statusDelivery && (
          <>
            {!delivery.isReceiverAddressError && (
              <InfoRow>
                <InfoValue large>
                  {delivery.renderRefinedAddressRawResultError && (
                    <span className="font-bold text-orange mr-5">
                      ({delivery.renderRefinedAddressRawResultError})
                    </span>
                  )}
                  {delivery.soojioneLimitedCustomized && (
                    <span className="font-bold text-white bg-orange mr-5">
                      주의{delivery.distanceRefineToLimitedRefined}
                    </span>
                  )}
                  {(delivery.spotKurlyDrink || delivery.mustSignature) && (
                    <span className="font-bold text-white bg-orange mr-5">
                      서명
                    </span>
                  )}
                  {delivery.receiverAddressRoadWithoutSigungu ||
                    delivery.receiverAddressWithoutSigungu ||
                    ""}{" "}
                  {delivery.receiverBuilding}
                  {delivery.receiverAddressRoadWithoutSigungu &&
                  delivery.receiverAddressWithoutSigungu
                    ? `/${delivery.receiverAddressWithoutSigungu}`
                    : ""}
                  {!delivery.receiverAddressRoadWithoutSigungu &&
                  delivery.receiverDong
                    ? `/${delivery.receiverDong}`
                    : ""}
                </InfoValue>
              </InfoRow>
            )}
            <InfoRow>
              <InfoValue large>{delivery.displayAddress}</InfoValue>
            </InfoRow>
          </>
        )}

        {delivery.statusPickup && (
          <>
            <InfoRow>
              <InfoValue large>
                {delivery.renderRefinedAddressRawResultError && (
                  <span className="font-bold text-orange mr-5">
                    ({delivery.renderRefinedAddressRawResultError})
                  </span>
                )}
                {delivery.senderAddressRoadWithoutSigungu}{" "}
                {delivery.senderBuilding}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoValue large>
                {delivery.deliveryPlace && delivery.typePickupReturn && (
                  <span className="font-bold text-white bg-orange mr-5">
                    보관등록
                  </span>
                )}
                {delivery.displaySenderAddress}
              </InfoValue>
            </InfoRow>
          </>
        )}

        {/** 번역 원문 */}
        {(delivery.statusPickup || delivery.statusDelivery) &&
          delivery.isTranslated && (
            <InfoRow>
              <InfoValue large>
                <span className="font-bold text-orange mr-5">
                  {delivery.isReceiverAddressError
                    ? "(번역오류주의)"
                    : "(영어원문)"}
                </span>
                {delivery.translatedOriginText}
              </InfoValue>
            </InfoRow>
          )}

        {!!delivery.totalProductName && delivery.typePickupReturn && (
          <ProductNameComponent delivery={delivery} />
        )}
      </div>

      {/** 현관 비밀번호 */}
      {(delivery.frontdoorPassword ||
        frontdoorPasswordsByAddress?.length > 0) &&
        (delivery.statusPickup || delivery.statusDelivery) && (
          <div className="p-10">
            <div className="text-18 font-bold mb-5">공동현관 출입정보</div>
            <FrontdoorPasswordButton
              delivery={delivery}
              onAfterSubmit={handleAfterSubmitFrontdoorPasswords}
              frontdoorPasswords={frontdoorPasswordsByAddress}
            />
          </div>
        )}

      {/** 위치 없음 */}
      {delivery.isReceiverAddressError && delivery.statusDelivery && (
        <div className="p-10 grid gap-5">
          {delivery.receiverMobile && (
            <SendMessageForRequestAddressButton delivery={delivery} />
          )}
          <ResetAddressButton
            delivery={delivery}
            type="receiver"
            onAfterSubmit={fetchById}
          />
        </div>
      )}

      {/** 번역 */}
      {(delivery.statusPickup || delivery.statusDelivery) &&
        (delivery.translatedResultOpenai ||
          delivery.translatedResultPapago) && (
          <div className="p-10 grid gap-5">
            {delivery.translatedResultOpenai && (
              <div className="text-black font-bold text-18">
                (번역1) {delivery.translatedResultOpenai.translatedText}
              </div>
            )}
            {delivery.refinedAfterTranslationResultOpenai &&
              delivery.isReceiverAddressError && (
                <UpdateAddressForTranslationButton
                  translatedPlatform="openai"
                  addressType="receiver"
                  bookId={delivery.bookId}
                  address={delivery.translatedResultOpenai?.translatedText}
                  label="번역1"
                  onAfterSubmit={fetchById}
                />
              )}
            {delivery.translatedResultPapago && (
              <div className="text-black font-bold text-18">
                (번역2) {delivery.translatedResultPapago.translatedText}
              </div>
            )}
            {delivery.refinedAfterTranslationResultPapago &&
              delivery.isReceiverAddressError && (
                <UpdateAddressForTranslationButton
                  translatedPlatform="papago"
                  addressType="receiver"
                  bookId={delivery.bookId}
                  address={delivery.translatedResultPapago?.translatedText}
                  label="번역2"
                  onAfterSubmit={fetchById}
                />
              )}
            {delivery.translatedResultOpenai && (
              <ReportTranslationErrorButton delivery={delivery} />
            )}
          </div>
        )}

      {/** 출발지 정보 */}
      <div className="p-10 text-gray-8">
        <div className="flex justify-between items-center">
          <div className="flex justify-start items-center">
            <div className="text-20 text-black">출</div>
            {(!hasCsesByBookIdNonChecked || delivery.typeNormal) &&
              delivery.statusDelivery && (
                <div className="ml-5 text-16">
                  {delivery.spotName}
                  {delivery.spot?.name ? `(${delivery.senderName})` : ""}
                </div>
              )}
          </div>
          <div className="flex justify-end items-center">
            {delivery.statusPickup &&
              delivery.typePickupReturn &&
              delivery.senderMobile && (
                <SendMessageButton
                  delivery={delivery}
                  active={!!delivery.statusPickup}
                  mobile={delivery.senderMobile}
                  type="pickupReturn"
                />
              )}

            {delivery.senderMobile && (
              <CallButton
                mobile={delivery.senderMobile}
                active={!!delivery.statusPickup}
                label="통화"
                bookId={delivery.bookId}
              />
            )}
          </div>
        </div>
        {((hasCsesByBookId && delivery.typePickupReturn) ||
          !delivery.statusDelivery) && (
          <div className="mt-10 grid gap-5">
            <div className="text-18">
              {delivery.spotName}
              {delivery.spot?.name ? `(${delivery.senderName})` : ""}
            </div>
            {(delivery.statusPickupCompleted ||
              delivery.statusDeliveryCompletedOrSentBackOrLost) && (
              <>
                <div className="text-18">
                  {delivery.refinedAddressRawResultError && (
                    <span className="font-bold text-orange mr-5">
                      {delivery.renderRefinedAddressRawResultError}
                    </span>
                  )}
                  {delivery.senderAddressRoadWithoutSigungu}{" "}
                  {delivery.senderBuilding}
                </div>
                <div className="text-18">
                  {delivery.deliveryPlace && delivery.typePickupReturn && (
                    <span className="font-bold text-white bg-orange mr-5">
                      보관등록
                    </span>
                  )}
                  {delivery.displaySenderAddress}
                </div>
              </>
            )}

            <ButtonsWrap>
              {/** 위치보기 */}
              <DeliveryLocationButton type="sender" />
              {/** 주소정보(픽반품건) */}
              {delivery.typePickupReturn && (
                <BaseAddressButton
                  delivery={delivery}
                  type="sender"
                  onAfterSubmit={fetchBaseAddressById}
                />
              )}
              {/** 경로찾기(픽반품건) */}
              {delivery.statusPickup &&
                delivery.typePickupReturn &&
                delivery.senderPoint && (
                  <DeliveryRouteButton delivery={delivery} type="sender" />
                )}
              {/** 수거 완료 */}
              {delivery.statusPickup && delivery.typeNormal && (
                <PickupButton delivery={delivery} />
              )}
            </ButtonsWrap>

            {/** 픽반품 수거 완료 */}
            {delivery.statusPickup && delivery.typePickupReturn && (
              <PickupCompleteComponent
                delivery={delivery}
                onAfterSubmit={fetchById}
              />
            )}

            {/** 주소 수정 */}
            {delivery.statusPickup && delivery.typePickupReturn && (
              <ButtonsWrap>
                <UpdateAddressButton
                  delivery={delivery}
                  type="sender"
                  onAfterSubmit={fetchById}
                />
              </ButtonsWrap>
            )}

            {/** CS */}
            {delivery.typePickupReturn && hasCsesByBookId && (
              <ButtonsWrap>
                {csesUserByBookId?.length > 0 && (
                  <CsButton type={CS_TYPE_USER} />
                )}
                {csesChainlogisByBookId?.length > 0 && (
                  <CsButton type={CS_TYPE_CHAINLOGIS} />
                )}
              </ButtonsWrap>
            )}

            {/** 파일재업로드 */}
            {delivery.statusPickupCompleted && isFailedFile && (
              <ButtonsWrap>
                <FailedFileReuploadButton delivery={delivery} type="pickup" />
              </ButtonsWrap>
            )}
          </div>
        )}
      </div>

      <div className="h-1 bg-gray-d" />

      {/** 도착지 정보 */}
      <div className="p-10 text-gray-8">
        <div className="flex justify-between items-center">
          <div className="flex justify-start items-center">
            <div className="text-20 text-black">도</div>
            {delivery.isHighPrice && (
              <div className="bg-orange w-16 h-16 rounded-full ml-5" />
            )}
            {delivery.statusDelivery && (
              <div className="ml-5 text-16" onClick={handleClickReceiverName}>
                {delivery.renderReceiverNameTrimmed}
              </div>
            )}
          </div>
          <div className="flex justify-end items-center">
            {delivery.statusDeliveryCompleted &&
              (delivery.receiverSmsMobile || delivery.receiverMobile) && (
                <UpdateDeliveryCompletedLocationButton
                  delivery={delivery}
                  onAfterSubmit={fetchById}
                />
              )}
            {(delivery.receiverSmsMobile || delivery.receiverMobile) && (
              <SendMessageButton
                delivery={delivery}
                active={!!delivery.statusDelivery}
                mobile={delivery.receiverSmsMobile || delivery.receiverMobile}
                type="receiver"
              />
            )}
            {delivery.receiverMobile && (
              <CallButton
                mobile={delivery.receiverMobile}
                active={!!delivery.statusDelivery}
                label="전화1"
                bookId={delivery.bookId}
              />
            )}
            {delivery.receiverMobile2 && (
              <CallButton
                mobile={delivery.receiverMobile2}
                active={!!delivery.statusDelivery}
                label="전화2"
                bookId={delivery.bookId}
              />
            )}
          </div>
        </div>
        <div className="mt-10">
          {!delivery.statusDelivery && (
            <div className="flex justify-between items-center mb-5">
              <div className="text-18">{delivery.renderReceiverName}</div>
            </div>
          )}
          <div className="mt-5 grid gap-5">
            {!delivery.isReceiverAddressError &&
              (delivery.statusPickupCompleted ||
                delivery.statusDeliveryCompletedOrSentBackOrLost) && (
                <div className="text-18">
                  {delivery.refinedAddressRawResultError && (
                    <span className="font-bold text-orange mr-5">
                      {delivery.renderRefinedAddressRawResultError}
                    </span>
                  )}
                  {delivery.soojioneLimitedCustomized && (
                    <span className="font-bold text-white bg-orange mr-5">
                      주의{delivery.distanceRefineToLimitedRefined}
                    </span>
                  )}
                  {(delivery.spotKurlyDrink || delivery.mustSignature) && (
                    <span className="font-bold text-white bg-orange mr-5">
                      서명
                    </span>
                  )}
                  {delivery.receiverAddressRoadWithoutSigungu ||
                    delivery.receiverAddressWithoutSigungu ||
                    ""}{" "}
                  {delivery.receiverBuilding}
                  {delivery.receiverAddressRoadWithoutSigungu &&
                  delivery.receiverAddressWithoutSigungu
                    ? `/${delivery.receiverAddressWithoutSigungu}`
                    : ""}
                  {!delivery.receiverAddressRoadWithoutSigungu &&
                  delivery.receiverDong
                    ? `/${delivery.receiverDong}`
                    : ""}
                </div>
              )}
            {(delivery.statusPickupCompleted ||
              delivery.statusDeliveryCompletedOrSentBackOrLost) && (
              <div className="text-18">{delivery.displayAddress}</div>
            )}

            {/** 배송 대기 상태  */}
            {(!hasCsesByBookIdNonChecked || delivery.typeNormal) &&
              delivery.statusWaiting && (
                <ButtonsWrap>
                  <DeliveryPostponedButton
                    delivery={delivery}
                    onAfterSubmit={fetchById}
                  />
                  <DeliveryStartButton
                    delivery={delivery}
                    onAfterSubmit={fetchById}
                  />
                </ButtonsWrap>
              )}

            {/** 배송 완료후 사진 업로드 실패 */}
            {delivery.statusDeliveryCompleted && !delivery.completedImage && (
              <>
                <div className="text-red">
                  사진 등록이 실패했습니다. 다시 등록해주십시오.
                </div>
                <DeliveryReUploadImageComponent delivery={delivery} />
              </>
            )}

            {/** 배송 완료 처리 */}
            {(!hasCsesByBookIdNonChecked || delivery.typeNormal) &&
              (delivery.statusDeliveryStart ||
                delivery.statusDeliveryPostponed) && (
                <DeliveryCompleteComponent delivery={delivery} />
              )}

            {/** 위치 / 주소정보 */}
            {(delivery.statusPickup ||
              delivery.statusDelivery ||
              delivery.hasMissingCompletedLocation) && (
              <ButtonsWrap>
                {/** 위치보기 */}
                <DeliveryLocationButton type="receiver" />
                {/** 주소정보 */}
                {delivery.typeNormal && (
                  <BaseAddressButton
                    delivery={delivery}
                    type="receiver"
                    onAfterSubmit={fetchBaseAddressById}
                  />
                )}
                {!delivery.isReceiverAddressError && (
                  <DeliveryRouteButton delivery={delivery} type="receiver" />
                )}
              </ButtonsWrap>
            )}

            {/** 주소 수정 */}
            {(!hasCsesByBookIdNonChecked || delivery.typeNormal) &&
              delivery.statusDelivery && (
                <ButtonsWrap>
                  <UpdateAddressButton
                    delivery={delivery}
                    type="receiver"
                    onAfterSubmit={fetchById}
                  />
                </ButtonsWrap>
              )}

            {/** 직접등록건 취소 */}
            {delivery.statusDeliveryStart && delivery.nonDoobalType && (
              <ButtonsWrap>
                <CancelDeliveryButton delivery={delivery} />
              </ButtonsWrap>
            )}

            {/** 배송불가 취소처리 */}
            {delivery.cancelableAddressNotSupported && (
              <ButtonsWrap>
                <CancelDeliveryNotSupportedButton
                  delivery={delivery}
                  onAfterSubmit={fetchById}
                />
              </ButtonsWrap>
            )}

            {/** 분실완료/반송완료 */}
            {(!hasCsesByBookIdNonChecked || delivery.typeNormal) &&
              (delivery.statusDeliveryStart ||
                delivery.statusDeliveryPostponed) && (
                <ButtonsWrap>
                  <DeliveryLostButton
                    delivery={delivery}
                    onAfterSubmit={fetchById}
                  />
                  <DeliverySentBackButton
                    delivery={delivery}
                    onAfterSubmit={fetchById}
                  />
                </ButtonsWrap>
              )}

            {/** CS */}
            {delivery.typeNormal && hasCsesByBookId && (
              <ButtonsWrap>
                {csesUserByBookId?.length > 0 && (
                  <CsButton type={CS_TYPE_USER} />
                )}
                {csesChainlogisByBookId?.length > 0 && (
                  <CsButton type={CS_TYPE_CHAINLOGIS} />
                )}
              </ButtonsWrap>
            )}

            {/** 배송 완료 상태 */}
            {delivery.statusDeliveryCompleted && (
              <>
                <ButtonsWrap>
                  {delivery.deliveryDoneLatitude &&
                    delivery.deliveryDoneLongitude && (
                      <DeliveryCompletedLocationButton
                        bgColor="bg-mint"
                        flex={2}
                        completedLatitude={delivery.deliveryDoneLatitude}
                        completedLongitude={delivery.deliveryDoneLongitude}
                        label="주소위치/완료위치"
                      />
                    )}
                  {/** 주소정보 */}
                  {delivery.typeNormal && (
                    <BaseAddressButton
                      delivery={delivery}
                      type="receiver"
                      onAfterSubmit={fetchBaseAddressById}
                    />
                  )}
                </ButtonsWrap>
                <ButtonsWrap>
                  {/** 배송 완료 취소 */}
                  <CancelDeliveryCompletedButton
                    delivery={delivery}
                    label="배송완료"
                    onAfterSubmit={fetchById}
                  />
                  {!isFailedFile && delivery.completedImage && (
                    <DeliveryCompletedImageButton
                      completedImage={delivery.completedImage}
                    />
                  )}
                </ButtonsWrap>
                {/** TODO: 파일재업로드 자동/수정 START */}
                {isFailedFile && delivery.statusDeliveryCompleted && (
                  <ButtonsWrap>
                    <FailedFileReuploadButton
                      delivery={delivery}
                      type="delivery"
                    />
                    <FailedFileReuploadAutoButton delivery={delivery} />
                  </ButtonsWrap>
                )}
                {/** TODO: 파일재업로드 자동/수정 END */}
              </>
            )}

            {/** 반송 완료 상태 */}
            {delivery.statusDeliverySentBack && (
              <ButtonsWrap>
                <CancelDeliveryCompletedButton
                  delivery={delivery}
                  label="반송완료"
                  onAfterSubmit={fetchById}
                />
                {delivery.deliverySentBackLatitude &&
                  delivery.deliverySentBackLongitude && (
                    <DeliveryCompletedLocationButton
                      completedLatitude={delivery.deliverySentBackLatitude}
                      completedLongitude={delivery.deliverySentBackLongitude}
                      label="주소위치/반송위치"
                    />
                  )}
              </ButtonsWrap>
            )}

            {/** 분실 완료 상태 */}
            {delivery.statusDeliveryLost && (
              <ButtonsWrap>
                <CancelDeliveryCompletedButton
                  delivery={delivery}
                  label="분실완료"
                  onAfterSubmit={fetchById}
                />
                {delivery.deliveryLostLatitude &&
                  delivery.deliveryLostLongitude && (
                    <DeliveryCompletedLocationButton
                      completedLatitude={delivery.deliveryLostLatitude}
                      completedLongitude={delivery.deliveryLostLongitude}
                      label="주소위치/분실위치"
                    />
                  )}
              </ButtonsWrap>
            )}
          </div>
        </div>
      </div>

      <div className="h-1 bg-gray-d" />

      {/** 송하인 정보 */}
      <div className="p-10 text-gray-8">
        <div className="flex justify-between items-center">
          <div className="flex justify-start items-center">
            <div className="text-20 text-black">송하인</div>
          </div>
        </div>
        <div className="mt-10">
          <div className="flex justify-between items-center mb-5">
            <div className="text-18">{delivery.senderName}</div>
            <div className="flex justify-end items-center">
              {delivery.senderMobile && (
                <CallButton
                  mobile={delivery.senderMobile}
                  label="통화"
                  bookId={delivery.bookId}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Delivery;
